import React, {useEffect, useMemo} from 'react'
import styled from "styled-components"
import {light, solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

const roomPadding = 10
const gridGap = 10

const VideoPage = styled.div`
    flex: 1 0 ${({pageWidth}) => (pageWidth - (roomPadding * 2)) + 'px'};
    height: ${({pageHeight, layout}) => (layout === 'spotlight' ? pageHeight + 'px' : pageHeight - (roomPadding * 2 + 75)) + 'px'};
    top: ${({layout}) => layout === 'spotlight' ? 0 : roomPadding + 'px'};
    left: ${({layout}) => layout === 'spotlight' ? 'auto' : roomPadding + 'px'};
    right: ${({layout}) => layout === 'spotlight' ? 0 : roomPadding + 'px'};
    bottom: ${({layout}) => layout === 'spotlight' ? 75 + 'px' : roomPadding + 'px'};
    overflow-y: ${({layout}) => layout === 'spotlight' ? 'auto' : 'none'};
    overflow-x: ${({layout}) => layout === 'spotlight' ? 'visible' : 'none'};
    display: flex;
    gap: ${gridGap}px;
    flex-flow: row wrap;
    justify-content: ${({justify}) => justify};
    align-items: center;
    align-content: ${({align}) => align};
    transition: all 250ms ease-in-out;
    border-left: ${({layout}) => layout === 'spotlight' ? '1px solid rgba(0,0,0,0.2)' : 0};
    padding: ${roomPadding}px;
    padding-left: ${({layout}) => layout === 'spotlight' ? gridGap * 2 + 'px' : `${roomPadding}px`};
    position: static;
`

const SpotlightSpacer = styled.div`
  flex: 1 1 100%;
`

const VideoWrapper = styled.div`
    width: ${({sizes}) => sizes.width}px;
    height: ${({sizes, layout}) => layout === 'spotlight' ? 'auto' : sizes.height + 'px'};
    aspect-ratio: ${({layout}) => layout === 'spotlight' ? '16/9' : 'auto'};
    /* PiP tiles are placed with coordinates measured from the #room box (Room's
       \`sizes\`), so they must be positioned against #room — 'fixed' would anchor them
       to the viewport instead and shift every tile by the 81px sidebar (big tile
       partly under the sidebar, corner tile off the room's edge). #room is itself
       positioned and VideoPage is static, so 'absolute' resolves to #room. */
    position: ${({spotlight, pip}) => pip ? 'absolute' : (spotlight ? 'fixed' : 'relative')};
    background: #000000;
    transition: all 250ms linear;
    top: ${({spotlight, pip, sizes}) => pip === 'big' ? `${sizes.top}px` : (pip === 'small' ? 'auto' : (spotlight ? '50%' : 'auto'))};
    left: ${({spotlight, pip, sizes}) => pip === 'big' ? `${sizes.left}px` : (pip === 'small' ? 'auto' : (spotlight ? `${roomPadding + 81}px` : 'auto'))};
    bottom: ${({pip}) => pip === 'small' ? '100px' : 'auto'};
    right: ${({pip}) => pip === 'small' ? `${roomPadding + 15}px` : 'auto'};
    z-index: ${({pip}) => pip === 'small' ? '10' : 'auto'};
    ${({pip}) => pip === 'small' && `
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid rgba(255,255,255,0.35);
        box-shadow: 0 4px 16px rgba(0,0,0,0.35);
    `}
    transform: ${({spotlight, mirrored}) => {
        const transform = []
        if(spotlight) {
            transform.push('translate(0, -50%)')
        }

        if(!mirrored) {
            transform.push('scaleX(-1)')
        }

        if(transform.length === 0) {
            transform.push('none')
        }

        return transform.join(' ')
    }};

    .OT_root, .OT_widget-container {
        width: 100%;
        height: 100%;
        overflow: hidden;

        img {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 50;
            min-height:20%;
            max-height: 50%;
        }
    }

    .OT_root {
        transition: all 250ms ease-in-out;
    }

    video {
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        transform-origin: 0 0;
        object-fit: cover};
    }

    .only_on_hover {
        opacity: 0;
        transition: opacity ease-in-out 200ms;
    }

    &:hover .only_on_hover{
        opacity: 1;
    }
`

const ScreenWrapper = styled.div`
    width: ${({sizes}) => sizes.width}px;
    height: ${({sizes}) => sizes.height}px;
    background: #000000;
    transition: all 250ms linear;
    position: fixed;
    top: ${roomPadding}px;
    left: ${roomPadding + 81}px;

    .OT_root, .OT_widget-container {
        width: 100%;
        height: 100%;
        overflow: hidden;

        img {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 50;
            min-height:20%;
            max-height: 50%;
        }
    }

    .OT_root {
        transition: all 250ms ease-in-out;
    }

    video {
        display: block;
        position: absolute;
        width: 100%;
        height: calc(100% - ${gridGap * 2}px);
        transform-origin: 0 0;
        object-fit: contain;
    }

    .only_on_hover {
        opacity: 0;
        transition: opacity ease-in-out 200ms;
    }

    &:hover .only_on_hover{
        opacity: 1;
    }
`

const NameWrapper = styled.span`
    padding: 0.25em 0.75em;
    border-radius: 10px;
    color: white;
    position: absolute;
    bottom: 5px;
    left: 5px;
    opacity: 0;
`

const PinWrapper = styled.span`
    padding: 0.5em 0.25em;
    border-radius: 50%;
    background: ${({active}) => active ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)"};
    color: ${({active}) => active ? "black" : "white"};
    border: 1px solid white;
    position: absolute;
    /* In pip the corner tile sits over the big tile's bottom-right corner, so a pin
       left there is unclickable (the corner tile swallows the mouse). Move the big
       tile's pin to the free top-right corner — the top-left holds the
       "Pinned by teacher" label and the bottom-left the name. */
    top: ${({pip}) => pip === 'big' ? '5px' : 'auto'};
    bottom: ${({pip}) => pip === 'big' ? 'auto' : '5px'};
    right: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    ${({active}) => active && `opacity: 1 !important;`}

`

const LobbyWrapper = styled.div`
    width: ${({sizes}) => sizes.width + 'px'};
    height: ${({sizes}) => sizes.height + 'px'};
    position: relative;
    background: #000000;
    display: flex;
    flex-flow: column nowrap;
    align-items: stretch;
    justify-content: center;
    padding: 15px;
    color: #ffffff;
    gap: 15px;
`

const videoWidth = (totalWidth, divisor, gap) => (totalWidth / divisor) - gap
const videoHeight = (width, totalWidth) => width * (totalWidth <= 687 ? (3/4) : (9/16))

const PinnedByTeacherLabel = styled.div`
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 5;
    padding: 4px 10px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.65);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    pointer-events: none;
    /* The publisher tile flips on scaleX(-1) when mirroring is off — counter it so the
       label stays readable. Subscriber tiles are never flipped (mirrored={true}). */
    transform: ${({flipped = false}) => flipped ? 'scaleX(-1)' : 'none'};
`

const Layout = React.forwardRef(({sizes, connectionCount, extraConnections, currentPage, setCurrentPage, mainCameraRef, mainCameraMirrored, screenShareRef, layoutStyle, Lobby, spotlightSession, subscriptions, audioPublished, videoPublished, screenShared, setSpotlight, activeScreenShareSubscription = false, moderator = false, forcedSpotlight = null, mainPublisherStreamId = ''}, ref) => {
    const videoColumns = useMemo(() => {
        if(!sizes || connectionCount === 1){
            return 1
        }

        if(connectionCount <= 4 || sizes.width <= 687){
            return 2
        }

        if((connectionCount <= 9 && connectionCount % 3 !== 1) || sizes.width <= 943){
            return 3
        }
        return 4
    }, [sizes, connectionCount])

    const containerHeight = useMemo(
        () => Math.floor((sizes ? sizes.height - (roomPadding * 2) - 85 : 0)),
        [sizes]
    );

    const pageWidth = useMemo(
        () => layoutStyle === 'spotlight' && sizes ? (sizes.width - (roomPadding * 2)) / 4 : (sizes ? sizes.width : 0),
        [layoutStyle, sizes]
    )

    const videoSize = useMemo(() => {
        if(!sizes){
            return {
                width: 0,
                height: 0,
                columns: videoColumns
            }
        }

        if(layoutStyle === 'spotlight' && sizes.width > 768){
            const width = ((sizes.width - roomPadding * 2) / 4) - (gridGap * 2)
            const height = videoHeight(width, sizes.width)
            return {
                width,
                height,
                columns: 1
            }
        }

        const width = videoWidth(
            sizes.width - (roomPadding * 2), // Container sans padding
            videoColumns,
            (gridGap * (videoColumns - 1)) / videoColumns // Amortized gap spacing
        )

        let height = videoHeight(width, sizes.width)

        const currentRows = Math.min(Math.ceil(connectionCount / videoColumns), 4);

        if((height * (currentRows) + (gridGap * currentRows)) > containerHeight){
            height = ((containerHeight - (gridGap * currentRows)) / currentRows)
        }
        return {
            width,
            height,
            columns: videoColumns
        }
    }, [sizes, videoColumns, connectionCount, layoutStyle])

    const maxRows = useMemo(
        () => {
            return Math.max(containerHeight / (videoSize && videoSize.height ? videoSize.height + gridGap : 1), 1)
        },
        [sizes, videoSize]
    )

    const currentRows = useMemo(
        () => Math.ceil(connectionCount / videoSize.columns),
        [connectionCount, videoSize]
    )

    const pages = useMemo(() => currentRows && maxRows ? Math.ceil(currentRows/maxRows) : 0, [currentRows, maxRows])
    const videosPerPage = useMemo(() => (maxRows * videoColumns), [maxRows, videoColumns])

    const spotlight = useMemo(() => {
        if((layoutStyle === 'spotlight' || layoutStyle === 'pip') && spotlightSession === 'main') {
            return 'mainPublisher'
        }else if(spotlightSession === 'screen'){
            return 'screen'
        }else if((layoutStyle === 'spotlight' || layoutStyle === 'pip') && spotlightSession){
            return spotlightSession
        }
    }, [layoutStyle, spotlightSession])

    // PiP (1-on-1): the big tile is the largest 16:9 rectangle that fits the room
    // (minus padding and the 85px control-bar allowance), centered; the small tile
    // is a fixed corner overlay. Both include explicit width/height (+ top/left for
    // big) consumed by VideoWrapper's pip css.
    const pipGeometry = useMemo(() => {
        if(!sizes){
            return {big: {width: 0, height: 0, top: 0, left: 0}, small: {width: 0, height: 0}}
        }
        const availWidth = sizes.width - (roomPadding * 2)
        const availHeight = sizes.height - (roomPadding * 2) - 85
        const bigWidth = Math.min(availWidth, availHeight * (16/9))
        const bigHeight = bigWidth * (9/16)
        const smallWidth = Math.min(200, sizes.width * 0.3)
        return {
            big: {
                width: bigWidth,
                height: bigHeight,
                top: roomPadding + ((availHeight - bigHeight) / 2),
                left: roomPadding + ((availWidth - bigWidth) / 2),
            },
            small: {
                width: smallWidth,
                height: smallWidth * (9/16),
            },
        }
    }, [sizes])

    // The sole remote camera in a 1-on-1 — the default "big" tile when no pin is set.
    const soleCameraKey = useMemo(() => {
        const keys = subscriptions
            ? Object.keys(subscriptions).filter(k => subscriptions[k].subscriber.stream.videoType !== 'screen')
            : []
        return keys.length === 1 ? keys[0] : null
    }, [subscriptions])

    // Only a target that actually has a pip tile can be the big one. A spotlight target
    // can outlive its tile — Room keeps spotlightSession = 'screen' after a screenshare
    // stops, and a pinned participant can leave — and an unmatched target left every
    // tile in the "small" corner role with no big tile at all.
    const pipBig = layoutStyle === 'pip'
        ? (spotlight === 'mainPublisher' ? 'mainPublisher' : soleCameraKey)
        : null

    const videoPages = useMemo(() => {
        const pageCollection = []

        if(layoutStyle === 'grid' || layoutStyle === 'spotlight' || layoutStyle === 'pip')
        {
            pageCollection.push({
                label: 1,
                entries: ['mainPublisher']
            })
        }

        if(layoutStyle === 'lobby')
        {
            pageCollection.push({
                label: 1,
                entries: ['mainPublisher', 'lobby']
            })
            return pageCollection;
        }

        if(screenShared)
        {
            pageCollection[0].entries.push('screenshare')
        }

        const keys = subscriptions ? Object.keys(subscriptions) : []
        let j = keys.length
        let k = extraConnections
        Array.from(Array(pages), (e, i) => {
            if(pageCollection.length === i){
                pageCollection.push({
                    label: i + 1,
                    entries: []
                });
            }

            while((pageCollection[i].entries.length < videosPerPage || layoutStyle === 'spotlight' || layoutStyle === 'pip') && j){
                const data = Object.fromEntries(new URLSearchParams(subscriptions[keys[j - 1]].subscriber.stream.connection.data))
                pageCollection[i].entries.push({
                    key: keys[j - 1],
                    video: subscriptions[keys[j - 1]].videoElement,
                    type: subscriptions[keys[j-1]].subscriber.stream.videoType,
                    ...data
                })
                j--;
            }

            while(pageCollection[i].entries.length < videosPerPage && !j && k){
                pageCollection[i].entries.push('extra')
                k--;
            }
        })

        return pageCollection
    }, [extraConnections, pages, videosPerPage, layoutStyle, subscriptions, spotlightSession, screenShared])

    useEffect(() => {
        if(pages === 0 || layoutStyle === 'spotlight' || layoutStyle === 'pip'){
            setCurrentPage(0)
        }else if(currentPage > pages - 1){
            setCurrentPage(pages - 1)
        }
    }, [pages]);

    return (<>
        {layoutStyle === 'spotlight' && (<SpotlightSpacer>
        </SpotlightSpacer>)}
        {videoPages.map((p, i) => (
            <VideoPage
                pageWidth={pageWidth}
                pageHeight={(sizes ? sizes.height : 0)}
                justify={p.entries.length < videoColumns || layoutStyle !== 'spotlight' ? 'flex-start' : 'center'}
                align={currentRows >= maxRows || layoutStyle === 'spotlight' ? 'flex-start' : 'center'}
                layout={layoutStyle}
            >
                {p.entries.map((v, j) => {
                    if(v === 'mainPublisher'){
                        // A forced pin of our OWN stream reaches Layout as the
                        // 'mainPublisher' token (Room's resolveSpotlightTarget maps the
                        // raw streamId), so the spotlight check below covers both the
                        // local self-pin and the forced self-pin. isForcedSelf compares
                        // the raw ids to tell the forced case apart for the label and
                        // the thumbtack's active state.
                        const isForcedSelf = !!(forcedSpotlight && mainPublisherStreamId && forcedSpotlight === mainPublisherStreamId)
                        const isSpotlighted = layoutStyle === 'spotlight' && spotlight === 'mainPublisher'
                        const pipRole = layoutStyle === 'pip' ? (pipBig === 'mainPublisher' ? 'big' : 'small') : null
                        return <VideoWrapper
                            id="mainCameraPublisher"
                            key='main'
                            ref={mainCameraRef}
                            sizes={pipRole ? pipGeometry[pipRole] : (isSpotlighted ? {width: sizes.width - pageWidth} : videoSize)}
                            spotlight={isSpotlighted}
                            pip={pipRole}
                            maxWidth={pageWidth - (gridGap * 2) - (sizes ? sizes.width : 0)}
                            mirrored={mainCameraMirrored}
                            layout={layoutStyle}
                        >
                            {isForcedSelf && (
                                <PinnedByTeacherLabel flipped={!mainCameraMirrored}>Pinned by teacher</PinnedByTeacherLabel>
                            )}
                            {moderator && mainPublisherStreamId && (
                                <PinWrapper active={isForcedSelf} pip={pipRole} className={'only_on_hover'} style={!mainCameraMirrored ? {transform: 'scaleX(-1)'} : undefined} onClick={() => setSpotlight(mainPublisherStreamId)}>
                                    {isForcedSelf && (<FontAwesomeIcon icon={solid('thumbtack')} size={'lg'} fixedWidth />)}
                                    {!isForcedSelf && (<FontAwesomeIcon icon={light('thumbtack')} size={'lg'} fixedWidth transform={{ rotate: -45 }} />)}
                                </PinWrapper>
                            )}
                        </VideoWrapper>
                    }

                    if(v === 'extra'){
                        return <VideoWrapper key={`${i}-${j}`} className='subscription' sizes={videoSize}
                        layout={layoutStyle}></VideoWrapper>
                    }

                    if(v === 'lobby'){
                        return (
                            <LobbyWrapper sizes={videoSize} key='lobby' layout={layoutStyle}>
                                <Lobby />
                            </LobbyWrapper>
                        )
                    }

                    if(v === 'screenshare'){
                        return (<ScreenWrapper
                            id='screenshare'
                            key='screen'
                            ref={screenShareRef}
                            sizes={layoutStyle === 'spotlight' && spotlight === 'screen' ? {
                                width: sizes.width - pageWidth,
                                height: sizes.height - (roomPadding * 2) - 81
                            } : videoSize}
                            spotlight={layoutStyle === 'spotlight' && spotlight === 'screen'}
                            maxWidth={pageWidth - (gridGap * 2) - (sizes ? sizes.width : 0)}
                            layout={layoutStyle}
                        ></ScreenWrapper>)
                    }

                    if(layoutStyle === 'spotlight' && spotlight === v.key && v.type === 'screen'){
                        return (<ScreenWrapper
                            key={v.key}
                            ref={screenShareRef}
                            sizes={{
                                width: sizes.width - pageWidth,
                                height: sizes.height - (roomPadding * 2) - 81
                            }}
                            spotlight={true}
                            mirrored={true}
                            maxWidth={pageWidth - (gridGap * 2) - (sizes ? sizes.width : 0)}
                            layout={layoutStyle}
                        >
                            <div className='OT_root OT_subscriber OT_fit-mode-cover'>
                                <div className='OT_widget-container' ref={ ref => ref && ref.appendChild(v.video)}></div>
                            </div>
                            <NameWrapper className={'only_on_hover'}>{v.name}</NameWrapper>
                        </ScreenWrapper>)
                    }

                    const pipRole = layoutStyle === 'pip' ? (pipBig === v.key ? 'big' : 'small') : null
                    return <VideoWrapper
                        className='subscription'
                        key={v.key}
                        sizes={pipRole ? pipGeometry[pipRole] : (layoutStyle === 'spotlight' && spotlight === v.key ? {width: ((sizes.width - (roomPadding * 2)) / 4) * 3} : videoSize)}
                        spotlight={layoutStyle === 'spotlight' && spotlight === v.key}
                        pip={pipRole}
                        maxWidth={pageWidth - (gridGap * 2) - (sizes ? sizes.width : 0)}
                        mirrored={true}
                        layout={layoutStyle}
                    >
                        <div className='OT_root OT_subscriber OT_fit-mode-cover'>
                            <div className='OT_widget-container' ref={ ref => ref && ref.appendChild(v.video)}></div>
                        </div>
                        <NameWrapper className={'only_on_hover'}>{v.name}</NameWrapper>
                        {forcedSpotlight && spotlight === v.key && (<PinnedByTeacherLabel>Pinned by teacher</PinnedByTeacherLabel>)}
                        <PinWrapper active={(layoutStyle === 'spotlight' || layoutStyle === 'pip') && spotlight === v.key} pip={pipRole} className={'only_on_hover'} onClick={() => setSpotlight(v.key)}>
                            {(layoutStyle === 'spotlight' || layoutStyle === 'pip') && spotlight === v.key && (<FontAwesomeIcon icon={solid('thumbtack')} size={'lg'} fixedWidth />)}
                            {!((layoutStyle === 'spotlight' || layoutStyle === 'pip') && spotlight === v.key) && (<FontAwesomeIcon icon={light('thumbtack')} size={'lg'} fixedWidth transform={{ rotate: -45 }} />)}
                        </PinWrapper>
                    </VideoWrapper>
                })}
            </VideoPage>
        ))}
    </>)
})

export default Layout
